import * as React from 'react';
import { useState, useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import logo from "../images/logo.png";
import { Link, useLocation } from 'react-router-dom';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import ContactUsPopup from "./ContactUsPopup";

const Navbar = () => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [drawerOpen, setDrawerOpen] = useState(false); // État pour le Drawer
    const location = useLocation(); // Pour obtenir l'URL actuelle
    const [scrolled, setScrolled] = useState(false); // État pour savoir si on a scrollé

    const [openPopup, setOpenPopup] = useState(false); // État pour contrôler l'ouverture de la popup

    const isInPrestationsPage = [
        '/electricite-generale',
        '/mobilite-electrique',
        '/energie-renouvelable',
        '/domotique',
        '/securite',
    ].includes(location.pathname);


    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
        if (event.type === 'keydown' && ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')) {
            return;
        }
        setDrawerOpen(open);
    };

    // Fonction pour gérer l'état de scroll
    const handleScroll = () => {
        if (window.scrollY > 450) { // On considère un scroll au-delà de 200px pour fixer l'AppBar
            setScrolled(true);
        } else {
            setScrolled(false);
        }
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll); // Nettoyage
        };
    }, []);

    const handleClickOpen = () => {
        setOpenPopup(true); // Ouvrir la popup
    };

    const handleClose = () => {
        setOpenPopup(false); // Fermer la popup
    };

    return (
        <AppBar
            position="fixed"
            sx={{
                backgroundColor: '#f6f8fb',
                boxShadow: scrolled ? '0px 5px 5px rgb(228,230,252, 0.7)' : 'none', // Ajout d'une ombre si scrolled
                backdropFilter: 'blur(5px)',
                zIndex: 10,
                transition: 'box-shadow 0.3s ease-in-out', // Transition douce
                margin: 'auto',
                left: 0,
                right: 0,
                width: { xs: '100%', sm: '100%', md: 'auto' },  // Pleine largeur sur petits écrans, auto sur grands
            }}
        >
            <Box
                sx={{
                    maxWidth: { xs: '100%', md: 'lg' }, // Adaptation de la largeur max sur petits/grands écrans
                    width: '100%',
                    margin: '0 auto',
                }}
            >
                <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative'  }}>

                    {/* Icône du menu hamburger visible uniquement sur petits écrans */}
                    <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="menu"
                            onClick={toggleDrawer(!drawerOpen)}
                            sx={{
                                color: 'black',
                                transform: drawerOpen ? 'rotate(180deg)' : 'rotate(0deg)', // Ajoute une rotation
                                transition: 'transform 0.3s ease-in-out', // Animation fluide
                            }}
                        >
                            <MenuIcon />
                        </IconButton>

                    </Box>

                    {/* Logo centré sur petits écrans */}
                    <Box
                        sx={{
                            display: { xs: 'flex', md: 'none' },
                            justifyContent: 'center',
                            flexGrow: 1,
                        }}
                    >
                        <Link to="/" style={{ textDecoration: 'none' }}> {/* Lien vers la page d'accueil */}
                            <Avatar
                                src={logo}
                                alt="Logo"
                                sx={{
                                    width: {
                                        xs: 75, // Taille réduite pour petits écrans
                                        md: 100
                                    },
                                    height: {
                                        xs: 75,
                                        md: 100
                                    }
                                }}
                            />
                        </Link>

                    </Box>

                    {/* Logo pour grands écrans, à gauche */}
                    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Link to="/" style={{ textDecoration: 'none' }}> {/* Lien vers la page d'accueil */}
                            <Avatar
                                src={logo}
                                alt="Logo"
                                sx={{ width: 85, height: 85 }}
                            />
                        </Link>
                    </Box>


                    {/* Menu principal pour grands écrans */}
                    <Box sx={{
                        display: { xs: 'none', md: 'flex' },
                        gap: 4,
                        justifyContent: 'center',  // Centre horizontalement
                        alignItems: 'center',      // Centre verticalement
                        flex: 1,                   // Prend tout l'espace disponible
                        position: 'absolute',      // Position absolue
                        left: '50%',              // Positionnement au centre
                        transform: 'translateX(-50%)', // Ajustement précis du centrage
                    }}>
                        <Button
                            component={Link}
                            to="/"
                            variant={location.pathname === '/' ? 'contained' : 'text'}
                            sx={{
                                //color: 'black',
                                fontFamily: 'unset',
                                textTransform: 'none',
                                fontSize: '1.2rem',
                                color: location.pathname === '/' ? 'white' : 'black',
                                backgroundColor: location.pathname === '/' ? '#3f72b5' : 'transparent',
                                padding: '2px 6px', // Réduire le rembourrage
                                minWidth: '90px', // Optionnel: largeur minimale
                                //borderRadius: 2,
                                '&:hover': {
                                    backgroundColor: location.pathname === '/' ? '#1c73a5' : '#e0e0e0', // Couleur de survol bleu foncé
                                },
                            }}
                        >
                            Accueil
                        </Button>

                        {/* Bouton pour les prestations avec menu */}
                        <Button
                            aria-controls="prestations-menu"
                            aria-haspopup="true"
                            onClick={handleMenuOpen}
                            sx={{
                                //color: 'black',
                                fontFamily: 'unset',
                                textTransform: 'none',
                                fontSize: '1.2rem',
                                color: isInPrestationsPage ? 'white' : 'black',
                                backgroundColor: isInPrestationsPage ? '#3f72b5' : 'transparent',
                                padding: '2px 6px', // Réduire le rembourrage
                                minWidth: '90px', // Optionnel: largeur minimale
                                //borderRadius: 6,
                                '&:hover': {
                                    backgroundColor: isInPrestationsPage ? '#1c73a5' : '#e0e0e0', // Couleur de survol blanc pour Nos Prestations
                                },
                            }}
                        >
                            Nos Prestations
                        </Button>
                        <Menu
                            id="prestations-menu"
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                            MenuListProps={{
                                'aria-labelledby': 'prestations-button',
                            }}
                            sx={{
                                '& .MuiPaper-root': {
                                    backgroundColor: 'rgba(251,251,251,0.9)',
                                    //borderRadius: '10px',
                                    padding: '2px 6px', // Réduire le rembourrage
                                    minWidth: '90px', // Optionnel: largeur minimale
                                    boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.15)',

                                },
                            }}
                        >
                            <MenuItem
                                component={Link}
                                to="/electricite-generale"
                                onClick={handleMenuClose}
                                sx={{fontFamily: 'unset', fontSize: '1.1rem'}}
                            >
                                Électricité générale
                            </MenuItem>
                            <MenuItem
                                component={Link}
                                to="/energie-renouvelable"
                                onClick={handleMenuClose}
                                sx={{fontFamily: 'unset', fontSize: '1.1rem'}}
                            >
                                Énergie renouvelable
                            </MenuItem>
                            <MenuItem
                                component={Link}
                                to="/mobilite-electrique"
                                onClick={handleMenuClose}
                                sx={{fontFamily: 'unset', fontSize: '1.1rem'}}
                            >
                                Mobilité électrique
                            </MenuItem>
                            <MenuItem
                                component={Link}
                                to="/domotique"
                                onClick={handleMenuClose}
                                sx={{fontFamily: 'unset', fontSize: '1.1rem'}}
                            >
                                Domotique
                            </MenuItem>
                            <MenuItem
                                component={Link}
                                to="/sécurité"
                                onClick={handleMenuClose}
                                sx={{fontFamily: 'unset', fontSize: '1.1rem'}}
                            >
                                Sécurité
                            </MenuItem>
                        </Menu>

                        <Button
                            component={Link}
                            to="/demande-partenariat"
                            variant={location.pathname === '/demande-partenariat' ? 'contained' : 'text'}
                            sx={{
                                //color: 'black',
                                fontFamily: 'unset',
                                textTransform: 'none',
                                fontSize: '1.2rem',
                                color: location.pathname === '/demande-partenariat' ? 'white' : 'black',
                                backgroundColor: location.pathname === '/demande-partenariat' ? '#3f72b5' : 'transparent',
                                padding: '2px 6px', // Réduire le rembourrage
                                minWidth: '90px', // Optionnel: largeur minimale
                                //borderRadius: 6,
                                '&:hover': {
                                    backgroundColor: location.pathname === '/demande-partenariat' ? '#1c73a5' : '#e0e0e0', // Couleur de survol bleu foncé
                                },
                            }}
                        >
                            Nous rejoindre
                        </Button>

                        <Button
                            //variant="contained"
                            onClick={handleClickOpen}
                            sx={{
                                //color: 'black',
                                fontFamily: 'unset',
                                textTransform: 'none',
                                fontSize: '1.2rem',
                                color: 'black',
                                backgroundColor: 'transparent',
                                padding: '2px 6px', // Réduire le rembourrage
                                minWidth: '90px', // Optionnel: largeur minimale
                                //borderRadius: 6,
                                '&:hover': {
                                    backgroundColor: '#e0e0e0' // Couleur de survol bleu foncé
                                },
                            }}
                        >
                            Contact
                        </Button>
                        <ContactUsPopup open={openPopup} onClose={handleClose} />
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: '#3f72b5',
                        fontSize: '1.5rem',
                    }}>
                        <LocalPhoneIcon sx={{ mr: 1, color: 'inherit' }} /> {/* Marge à droite pour espacer */}
                        <a href="tel:0698281814" style={{ textDecoration: 'none', color: 'inherit' }}>
                            06 98 28 18 14
                        </a>
                    </Box>
                </Toolbar>
            </Box>

            {/* Drawer pour les petits écrans */}
            <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
                <List sx={{ width: 250 }}>
                    <ListItem button component={Link} to="/" onClick={toggleDrawer(false)}>
                        <ListItemText
                            primary="Accueil"
                            sx={{ marginLeft: 2, fontFamily: 'Roboto, sans-serif' }}
                        />
                    </ListItem>
                    <ListItem button component={Link} to="/electricite-generale" onClick={toggleDrawer(false)}>
                        <ListItemText
                            primary="Électricité générale"
                            sx={{ marginLeft: 2, fontFamily: 'Roboto, sans-serif' }}
                        />
                    </ListItem>
                    <ListItem button component={Link} to="/mobilite-electrique" onClick={toggleDrawer(false)}>
                        <ListItemText
                            primary="Mobilité électrique"
                            sx={{ marginLeft: 2, fontFamily: 'Roboto, sans-serif' }}
                        />
                    </ListItem>
                    <ListItem button component={Link} to="/energie-renouvelable" onClick={toggleDrawer(false)}>
                        <ListItemText
                            primary="Énergie renouvelable"
                            sx={{ marginLeft: 2, fontFamily: 'Roboto, sans-serif' }}
                        />
                    </ListItem>
                    <ListItem button component={Link} to="/domotique" onClick={toggleDrawer(false)}>
                        <ListItemText
                            primary="Domotique"
                            sx={{ marginLeft: 2, fontFamily: 'Roboto, sans-serif' }}
                        />
                    </ListItem>
                    <ListItem button component={Link} to="/sécurité" onClick={toggleDrawer(false)}>
                        <ListItemText
                            primary="Sécurité"
                            sx={{ marginLeft: 2, fontFamily: 'Roboto, sans-serif' }}
                        />
                    </ListItem>
                    <ListItem button component={Link} to="/demande-partenariat" onClick={toggleDrawer(false)}>
                        <ListItemText
                            primary="Nous rejoindre"
                            sx={{ marginLeft: 2, fontFamily: 'Roboto, sans-serif' }}
                        />
                    </ListItem>
                </List>
            </Drawer>

        </AppBar>
    );
};

export default Navbar;
